import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import { formatPrice } from "../../../../utilities/formatPrice";
import AdminService from "../../../../service/AdminService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { buildSearchText, formatDay, getAdminUsername, getToken, normalizeText } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import SearchInput from "../../components/list/SearchInput";
import StatusBadge from "../../components/list/StatusBadge";
import EmptyState from "../../components/list/EmptyState";
import CopyValue from "../../components/list/CopyValue";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";

const TABS = {
  PENDING: "PROCESSING",
  COMPLETED: "COMPLETED",
  ALL: "all",
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const money = (row, value) => `${row.currency || ""} ${formatPrice(value ?? 0)}`.trim();

const loadWithdrawRequests = async () => {
  const res = await new AdminService().getAllWithdrawRequests(getToken(), getAdminUsername());
  return expectCode(res, 200001).result;
};

const AdminWithdrawRequests = () => {
  const { items, loading, loadError, reload } = useAdminList(loadWithdrawRequests);
  const { run, runningId } = useAdminAction();
  const [tab, setTab] = useState(TABS.PENDING);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const rows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        isPaypal: !!row.paypalEmailToTransfer,
        searchText: buildSearchText([
          row.paypalEmailToTransfer,
          row.accountOwner,
          row.bankname,
          row.iban,
          row.userprocess?.username,
          row.userprocess?.firstname,
          row.userprocess?.surname,
        ]),
      })),
    [items],
  );

  const counts = useMemo(
    () => ({
      [TABS.PENDING]: rows.filter((row) => row.status === TABS.PENDING).length,
      [TABS.COMPLETED]: rows.filter((row) => row.status === TABS.COMPLETED).length,
      [TABS.ALL]: rows.length,
    }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    return rows.filter(
      (row) =>
        (tab === TABS.ALL || row.status === tab) && (!term || row.searchText.includes(term)),
    );
  }, [rows, tab, search]);

  const processSelected = async () => {
    const ok = await run(
      selected.id,
      async () =>
        expectCode(
          await new AdminService().postProcessWithdraw(getToken(), selected.id, getAdminUsername()),
          200001,
        ),
      "withdrawProcessedSuccessfully",
    );
    setSelected(null);
    if (ok) reload();
  };

  const destinationTemplate = (row) =>
    row.isPaypal ? (
      <div className="admin-stack">
        <span className="admin-muted">PayPal</span>
        <CopyValue value={row.paypalEmailToTransfer} />
      </div>
    ) : (
      <div className="admin-stack">
        <span className="admin-strong">{row.accountOwner || "—"}</span>
        <span className="admin-muted">{row.bankname}</span>
        <CopyValue value={row.iban} mono />
      </div>
    );

  const statusTemplate = (row) => {
    if (row.status === TABS.COMPLETED) {
      return <StatusBadge tone="good">{localeText("completed")}</StatusBadge>;
    }
    if (row.status === TABS.PENDING) {
      return <StatusBadge tone="warning">{localeText("processing")}</StatusBadge>;
    }
    return <StatusBadge tone="neutral">{row.status}</StatusBadge>;
  };

  const hasFilters = !!search.trim();

  return (
    <div className="admin-list">
      <ConfirmActionDialog
        visible={!!selected}
        message={localeText("processThisRequest")}
        details={
          selected
            ? [
                {
                  label: localeText("adminWithdrawDestination"),
                  value: selected.isPaypal
                    ? `PayPal · ${selected.paypalEmailToTransfer}`
                    : `${selected.accountOwner || ""} · ${selected.bankname || ""}`,
                },
                { label: "IBAN", value: selected.isPaypal ? null : selected.iban },
                { label: localeText("amountRequested"), value: money(selected, selected.amountRequested) },
                { label: capitalize(localeText("withdrawFee")), value: money(selected, selected.fee) },
                { label: localeText("amountToDeposit"), value: money(selected, selected.amountToDeposit) },
              ]
            : []
        }
        confirmLabel={localeText("process")}
        loading={!!selected && runningId === selected.id}
        onConfirm={processSelected}
        onCancel={() => setSelected(null)}
      />

      <StatusTabs
        tabs={[
          { value: TABS.PENDING, label: localeText("processing") },
          { value: TABS.COMPLETED, label: localeText("completed") },
          { value: TABS.ALL, label: localeText("adminUsersStatusAll") },
        ]}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={loading}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("searchByNameSurnameUserAccountBank")}
          />
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminUsersResults")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            dataKey="id"
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            sortField="dateRequest"
            sortOrder={-1}
            removableSort
            scrollable
            tableStyle={{ minWidth: "48rem" }}
            emptyMessage={
              loadError ? (
                <EmptyState
                  icon="pi pi-exclamation-triangle"
                  message={localeText("adminListLoadError")}
                  actionLabel={localeText("adminUsersRetry")}
                  onAction={reload}
                />
              ) : (
                <EmptyState
                  icon={tab === TABS.PENDING && !hasFilters ? "pi pi-check-circle" : "pi pi-inbox"}
                  message={localeText(
                    hasFilters
                      ? "adminListNoResults"
                      : tab === TABS.PENDING
                        ? "adminWithdrawNoPending"
                        : "adminListEmpty",
                  )}
                  actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
                  onAction={() => setSearch("")}
                />
              )
            }
          >
            <Column header={localeText("adminWithdrawDestination")} body={destinationTemplate} />
            <Column
              header={localeText("requestDate")}
              field="dateRequest"
              body={(row) => formatDay(row.dateRequest)}
              sortable
            />
            {/* Valores numa coluna só: o que será depositado em destaque, solicitado e taxa abaixo */}
            <Column
              header={localeText("amountToDeposit")}
              field="amountToDeposit"
              body={(row) => (
                <div className="admin-stack">
                  <span className="admin-num admin-strong">{money(row, row.amountToDeposit)}</span>
                  <span className="admin-num admin-muted">
                    {localeText("adminWithdrawRequestedShort")} {money(row, row.amountRequested)}
                  </span>
                  <span className="admin-num admin-muted">
                    {capitalize(localeText("withdrawFee"))} {money(row, row.fee)}
                  </span>
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("status")}
              field="status"
              body={(row) => (
                <div className="admin-stack admin-stack--gap">
                  {statusTemplate(row)}
                  {row.userprocess?.username && (
                    <span className="admin-muted">
                      @{row.userprocess.username}
                      {row.dateProcess ? ` · ${formatDay(row.dateProcess)}` : ""}
                    </span>
                  )}
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(row) =>
                row.status === TABS.PENDING ? (
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={() => setSelected(row)}
                    disabled={runningId === row.id}
                  >
                    <i
                      className={runningId === row.id ? "pi pi-spin pi-spinner" : "pi pi-check"}
                      aria-hidden="true"
                    />
                    {localeText("process")}
                  </button>
                ) : null
              }
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminWithdrawRequests;
